import { generatePath } from 'react-router-dom'

import { NAV_ITEMS, AP_ITEMS, AR_ITEMS, CONTRACT_ITEMS } from '@components/Navigation/menuItems'
import { filterNavItems } from '@components/Navigation/utils'
import type { NavFilterUser } from '@components/Navigation/utils'

import { ContactParty } from '@models/ApproveitAccounting'
import routes from '@src/Routes/routes'

/**
 * Compute the route a user should land on when:
 *   - they just logged in, or
 *   - they hit a perm-gated route they don't have access to.
 *
 * Walks the same nav-item lists the sidebar renders, picks the first
 * one the user passes filterNavItems for, and returns its `to`. The
 * Dashboard entry is intentionally skipped — if the caller is the
 * ProtectedRoute fallback fired because Dashboard itself was gated,
 * returning `/dashboard` here would loop. We want the user's first
 * REAL work surface.
 *
 * Final fallback is the AI Assistant route — it has no perm gate (teams
 * without the chatbot flag get the locked beta teaser, still a valid
 * page), so it's the closest thing to a guaranteed-available surface.
 * `assistantAvailable` should be `!ai_assistant_hidden` (the escape
 * hatch removes the assistant surface entirely; the page component
 * bounces deep links away, so landing there would loop). When the
 * assistant is hidden AND no nav item passes, fall back to the personal
 * settings route — auth-only on both shells (web redirects /settings →
 * /profile; the Teams routers render SettingsPage), so a zero-permission
 * user gets a real page instead of a redirect loop.
 *
 * Pure function. Callers should pass the freshest user object they
 * have; we don't read context here.
 */
export function getLandingRoute(
  user: NavFilterUser | undefined,
  assistantAvailable = true,
  showCustomers = true,
): string {
  if (!user) return routes.private.assistant
  // Mirror the LeftNav restriction shape — the sidebar hides `/assistant`
  // only for ai_assistant_hidden teams (the chatbot flag just locks it).
  const navRestrictions = { [routes.private.assistant]: assistantAvailable }
  const candidates = [
    ...filterNavItems(NAV_ITEMS, user, navRestrictions),
    // Mirror LeftNav/BurgerMenu: the vendor/customer contacts entries are
    // hidden by the showCustomers team flag, so apply the same per-route
    // restriction here — otherwise login/redirect could land a user on a
    // contacts route the sidebar intentionally hides.
    ...filterNavItems(AP_ITEMS, user, {
      [generatePath(routes.private.contacts, { contactParty: ContactParty.vendor })]: showCustomers,
    }),
    ...filterNavItems(AR_ITEMS, user, {
      [generatePath(routes.private.contacts, { contactParty: ContactParty.customer })]: showCustomers,
    }),
    ...filterNavItems(CONTRACT_ITEMS, user),
  ]
  // Exclude `/assistant` from primary picks too: it appears early in
  // NAV_ITEMS and would otherwise win over genuine work surfaces. We
  // want it ONLY as the final fallback below, not as the default
  // pick for a user who has real tabs available.
  const firstReal = candidates.find(
    (item) => item.to && item.to !== routes.private.dashboard && item.to !== routes.private.assistant,
  )
  // Final fallback: `/assistant` when the surface exists (locked teaser is
  // fine); otherwise personal settings. NEVER `/dashboard` here — it is
  // request.view-gated, so a zero-permission user would bounce back through
  // this function forever.
  return firstReal?.to ?? (assistantAvailable ? routes.private.assistant : routes.private.settings)
}
